import UrlPattern from 'url-pattern';
import qs from 'qs';
import getConfig from 'next/config';

import {
  LANGUAGE_DE,
  LANGUAGE_FR,
  Languages,
} from '@common/constants/languages';
import { frenchUrlsRouteMap, germanUrlsRouteMap } from '@common/routes.mjs';
import { LinkUrlObject } from '@common/types/link';

import { findRouteByPath } from './findRouteByPath';

const { FRONTEND_BASE_URL: defaultDomain } = getConfig()
  ?.publicRuntimeConfig || {
  FRONTEND_BASE_URL: process.env.FRONTEND_BASE_URL,
};

export const getPathUrl = (path: string, domain = defaultDomain) => {
  if (domain.slice(-1) === '/') {
    return `${domain.slice(0, -1)}${path}`;
  }

  return `${domain}${path}`;
};

function getUrlsMapByLanguage(language: Languages) {
  switch (language) {
    case LANGUAGE_FR:
      return frenchUrlsRouteMap;
    case LANGUAGE_DE:
    default: {
      return germanUrlsRouteMap;
    }
  }
}

type UrlsMapType = Record<string, (params?: unknown) => string>;

export function getRouteUrl(language: Languages, route: string, params = {}) {
  const urlsMap: UrlsMapType = getUrlsMapByLanguage(language);
  let routePath;

  if (!urlsMap[route]) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.info(
        `Missing translated url for route: ${route} in language: ${language}`,
      );
    }

    routePath = route;
  } else {
    routePath = urlsMap[route](params);
  }

  return `/${language}${routePath}`;
}

const URL_SEGMENTS = new UrlPattern(
  '(http(s)\\://)(:subdomain.)(:domain.)(:tld)(\\::port)/:lang(/*)',
);

export const getLinkDataFromUrl = (
  url = '',
): {
  as: string;
  urlObject: LinkUrlObject;
} | null => {
  const urlSegments = URL_SEGMENTS.match(url);

  if (!urlSegments) {
    return null;
  }

  const { lang, _: pathWithQuery = '' } = urlSegments;

  const [path, query] = `/${pathWithQuery}`.split('?');
  const queryFromUrl = qs.parse(query);

  const { pathname, pattern } = findRouteByPath(path, lang);

  return pathname
    ? {
        as: `/${lang}${path}`,
        urlObject: {
          pathname,
          query: {
            ...queryFromUrl,
            ...pattern?.match(path),
          },
        },
      }
    : null;
};
